'use client';

import { Suspense, useCallback, useState } from 'react';
import dynamic from 'next/dynamic';
import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';
import { tryNativeShare } from '@/src/lib/utils/shareUtils';
import { useOptionalListingActionContext } from '@/src/components/features/listing-actions';
import type { ShareClickstreamContext } from '@/src/components/features/listing-actions/listingActionUtils';
import { analytics } from '@/src/components/features/property/lib';
import { emitTrackHdpNativeShareEvent } from '@/src/analytics/hotpads/events';
import { toHotpadsClickstreamListing } from '@/src/analytics/hotpads/track';

const ShareFormOverlay = dynamic(() =>
  import('@/src/components/features/property/ShareForm/ShareFormOverlay').then(
    (mod) => mod.ShareFormOverlay,
  ),
);

/**
 * Compatibility hook for existing share call sites. ListingActionProvider hosts
 * the share modal once so cards/modules no longer mount their own portals.
 *
 * On mobile devices with the Web Share API we defer to the native share
 * sheet — it's the most familiar UX and hooks into installed apps. Every
 * other context (desktop, and mobile without Web Share) opens ShareForm
 * inside the Constellation Modal hosted by ListingActionProvider.
 *
 * Accepts ExtendedListing so it works from both SRP (FeedCard) and HDP
 * contexts; the overlay itself only needs PropertyDetailListing-compatible
 * fields (ExtendedListing is a structural superset of what ShareForm reads).
 */
export function useShareAction(
  listing: ExtendedListing | null | undefined,
  shareClickstreamContext: ShareClickstreamContext,
) {
  const listingActionContext = useOptionalListingActionContext();
  const [isOpen, setIsOpen] = useState(false);

  const openShare = useCallback(() => {
    if (!listing) return;

    if (listingActionContext) {
      listingActionContext.openShare(listing, shareClickstreamContext);
      return;
    }

    const clickstreamListing = toHotpadsClickstreamListing(listing);
    analytics.emitTrackShareClickEvent({
      listing: clickstreamListing,
      ...shareClickstreamContext,
    });
    if (tryNativeShare(listing)) {
      emitTrackHdpNativeShareEvent({
        listing: clickstreamListing,
        triggerLocation: shareClickstreamContext.triggerLocation,
      });
      return;
    }

    setIsOpen(true);
  }, [listing, listingActionContext, shareClickstreamContext]);

  if (listingActionContext) {
    return { openShare, shareFormOverlay: null };
  }

  const shareFormOverlay =
    isOpen && listing ? (
      <Suspense fallback={null}>
        <ShareFormOverlay open={isOpen} onOpenChange={setIsOpen} listing={listing} triggerLocation={shareClickstreamContext.triggerLocation} />
      </Suspense>
    ) : null;

  return { openShare, shareFormOverlay };
}
